/**
 * Date utility functions for consistent formatting across the application
 */

/**
 * Format a Date object as a local-date string in YYYY-MM-DD for Hasura/Postgres `date` columns.
 *
 * IMPORTANT: Never use `.toISOString().split('T')[0]` to serialize a picker-selected date.
 * MUI DatePicker returns a `Date` at local midnight. For Europe/Istanbul (UTC+3),
 * `toISOString()` converts that to UTC — pushing the day back 3 hours to the previous
 * calendar day. This helper reads the local Y/M/D directly and avoids any UTC math.
 *
 * @param {Date|string|null|undefined} value
 * @returns {string|null} `YYYY-MM-DD` or `null`
 */
export const formatDateForHasura = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// TIMESTAMPTZ/TIMESTAMP kolonlar icin: lokal Y/M/D'yi explicit UTC 00:00 ISO
// olarak yollar. Postgres container timezone'undan bagimsiz gunu korur.
export const formatDateForTimestamptz = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

/**
 * Convert DB / JSON values into a `Date` for MUI DatePicker / DateTimePicker.
 * Hasura `date` columns arrive as `YYYY-MM-DD` — parsed as local midnight so
 * the displayed day never shifts due to UTC (inverse of formatDateForHasura).
 *
 * @param {Date|string|null|undefined} value
 * @returns {Date|null}
 */
export const parseDateForForm = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    // Timezone-suffix'li ISO (`Z` veya `+03:00`) → UTC parse, lokal Y/M/D al.
    // Bu, `JSON.stringify(Date)`'in UTC'ye kaydirdigi degerleri geri kazanir.
    if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(value)) {
      const utc = new Date(value);
      if (isNaN(utc.getTime())) return null;
      return new Date(utc.getFullYear(), utc.getMonth(), utc.getDate());
    }
    // Naked date/datetime (`2026-07-31T00:00:00` gibi) → ilk 10 karakter lokal.
    const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      const [, year, month, day] = ymdMatch;
      const local = new Date(Number(year), Number(month) - 1, Number(day));
      return isNaN(local.getTime()) ? null : local;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

/**
 * Format date to Turkish locale
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateTR = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('tr-TR');
};

export const formatDateShort = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Format date to English locale
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateEN = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US');
};

/**
 * Get current locale date formatter
 * @param {string} locale - Locale string ('tr' or 'en')
 * @returns {Function} Date formatter function
 */
export const getDateFormatter = (locale = 'tr') => {
  return locale === 'tr' ? formatDateTR : formatDateEN;
};

/**
 * Format date with time
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale string
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, locale = 'tr') => {
  if (!date) return '';
  const d = new Date(date);
  const pad = n => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Calculate hourly leave duration between two dates
 * Excludes time between 17:30 and 08:30 (next day) from calculation
 * Only counts working hours: 08:30 - 17:30
 * Example: 1 Jan 12:00 - 2 Jan 08:30 = 5.5 hours (12:00-17:30 on 1 Jan, 17:30-08:30 excluded)
 * @param {Date|string} startDate - Start date with time
 * @param {Date|string} endDate - End date with time
 * @returns {string} Duration in hours and minutes format
 */
export const calculateHourlyLeaveDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (end <= start) {
    return '0 dakika';
  }

  const WORK_START_HOUR = 8;
  const WORK_START_MINUTE = 30;
  const WORK_END_HOUR = 17;
  const WORK_END_MINUTE = 30;

  let totalMinutes = 0;
  
  // Get start and end dates at midnight for day comparison
  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  
  // Iterate through each day from start to end
  const currentDay = new Date(startDay);
  
  while (currentDay <= endDay) {
    const dayOfWeek = currentDay.getDay();
    
    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      currentDay.setDate(currentDay.getDate() + 1);
      continue;
    }

    // Set work start and end times for current day
    const workStart = new Date(currentDay);
    workStart.setHours(WORK_START_HOUR, WORK_START_MINUTE, 0, 0);
    
    const workEnd = new Date(currentDay);
    workEnd.setHours(WORK_END_HOUR, WORK_END_MINUTE, 0, 0);

    // Determine the effective start time for this day
    let effectiveStart;
    if (currentDay.getTime() === startDay.getTime()) {
      // First day: use actual start time, but not before 08:30
      effectiveStart = start > workStart ? start : workStart;
    } else {
      // Middle days: start from 08:30
      effectiveStart = workStart;
    }

    // Determine the effective end time for this day
    let effectiveEnd;
    if (currentDay.getTime() === endDay.getTime()) {
      // Last day: use actual end time, but not after 17:30
      // If end is at or before 08:30, don't count this day (no work hours)
      if (end <= workStart) {
        currentDay.setDate(currentDay.getDate() + 1);
        continue;
      }
      effectiveEnd = end < workEnd ? end : workEnd;
    } else {
      // Middle days: end at 17:30
      effectiveEnd = workEnd;
    }

    // Only count if effective start is before effective end and within work hours
    if (effectiveStart < effectiveEnd && effectiveStart >= workStart && effectiveEnd <= workEnd) {
      const diffMs = effectiveEnd - effectiveStart;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes > 0) {
        totalMinutes += diffMinutes;
      }
    }

    // Move to next day
    currentDay.setDate(currentDay.getDate() + 1);
  }

  // Convert total minutes to hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = '';
  if (hours > 0) result += `${hours} saat`;
  if (minutes > 0) result += (result ? ' ' : '') + `${minutes} dakika`;
  if (!result) result = '0 dakika';
  
  return result;
};

/**
 * Calculate duration between two dates
 * Excludes weekends (Saturday and Sunday) from calculation
 * Special rule: If leave starts on Monday, duration is 6 days for:
 * - Monday to Saturday (6 days)
 * - Monday to Sunday (6 days)
 * - Monday to Monday (next week, 7 days difference = 6 working days)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date (return to work date, exclusive)
 * @param {boolean} isHourlyLeave - Whether this is an hourly leave (excludes 17:30-08:30)
 * @returns {string} Duration in a human-readable format
 */
export const calculateDuration = (startDate, endDate, isHourlyLeave = false) => {
  // If it's an hourly leave, use special calculation
  if (isHourlyLeave) {
    return calculateHourlyLeaveDuration(startDate, endDate);
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const diffMs = end - start;
  
  const diffMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
  const days = Math.floor(diffMinutes / (60 * 24));
  const hours = Math.floor((diffMinutes % (60 * 24)) / 60);
  const minutes = diffMinutes % 60;
  
  // If there are hours or minutes, or it's same day, return time-based duration
  if (hours > 0 || minutes > 0 || days === 0) {
    let result = '';
    if (days > 0) result += `${days} gün`;
    if (hours > 0) result += (result ? ' ' : '') + `${hours} saat`;
    if (minutes > 0) result += (result ? ' ' : '') + `${minutes} dakika`;
    if (!result) result = '0 dakika';
    return result;
  }
  
  // For full day differences, calculate working days (excluding weekends)
  // Set time to midnight for accurate day comparison
  const startForDayCheck = new Date(start);
  const endForDayCheck = new Date(end);
  startForDayCheck.setHours(0, 0, 0, 0);
  endForDayCheck.setHours(0, 0, 0, 0);
  
  const startDayOfWeek = startForDayCheck.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const endDayOfWeek = endForDayCheck.getDay();
  
  // Calculate difference in days
  const diffDaysForCheck = Math.floor(Math.abs(endForDayCheck - startForDayCheck) / (1000 * 60 * 60 * 24));
  const diffMsForCheck = endForDayCheck - startForDayCheck;
  
  // Special rule: If leave starts on Monday (1)
  if (startDayOfWeek === 1 && diffMsForCheck >= 0) {
    // Monday to Saturday (6 days later) = 6 working days
    if (endDayOfWeek === 6 && diffDaysForCheck === 6) {
      return '6 gün';
    }
    // Monday to Sunday (7 days later) = 6 working days
    if (endDayOfWeek === 0 && diffDaysForCheck === 7) {
      return '6 gün';
    }
    // Monday to Monday (next week, 7 days later) = 6 working days
    if (endDayOfWeek === 1 && diffDaysForCheck === 7) {
      return '6 gün';
    }
    
    // Monday to next week's weekday (Tuesday=2, Wednesday=3, Thursday=4, Friday=5)
    // Formula: 6 days (first week) + (weekday number - 1)
    // Only applies to the immediate next week (8-11 days), not beyond
    if (endDayOfWeek >= 1 && endDayOfWeek <= 5 && diffDaysForCheck >= 8 && diffDaysForCheck <= 11) {
      const additionalDays = endDayOfWeek - 1; // Monday=0, Tuesday=1, etc.
      const totalDays = 6 + additionalDays;
      return `${totalDays} gün`;
    }
    
    // Monday to Monday (2 weeks later, 14 days difference) = 12 days (6 + 6)
    // Each week counts as 6 days (Monday to Saturday)
    if (endDayOfWeek === 1 && diffDaysForCheck === 14) {
      return '12 gün';
    }
    
    // Monday to Monday (multiple weeks later)
    // Calculate: (number of weeks) * 6 days
    if (endDayOfWeek === 1 && diffDaysForCheck > 7 && diffDaysForCheck % 7 === 0) {
      const weeks = diffDaysForCheck / 7;
      const totalDays = weeks * 6;
      return `${totalDays} gün`;
    }
    
    // Monday to weekday after 2 weeks (15+ days)
    // Formula: 12 days (2 weeks) + (weekday number - 1)
    if (endDayOfWeek >= 1 && endDayOfWeek <= 5 && diffDaysForCheck >= 15) {
      const weeks = Math.floor(diffDaysForCheck / 7);
      const baseDays = weeks * 6; // Each week = 6 days
      const additionalDays = endDayOfWeek - 1; // Monday=0, Tuesday=1, etc.
      const totalDays = baseDays + additionalDays;
      return `${totalDays} gün`;
    }
  }
  
  // Leave starting Tuesday-Friday and crossing at least one Monday
  // (Ibrahim, 2026-11-02): "pazartesiden pazartesiye 6 gun, onceki haftadan
  // da 1 gelsin." Weeks are 6-day (Mon-Sat) blocks like the Monday rule above;
  // the partial week BEFORE the first Monday counts weekdays only
  // (Fri 20.11 -> Mon 30.11 = 1 + 6 = 7). A trailing partial week counts
  // Monday up to the day before the return date, same as the Monday rule.
  if (startDayOfWeek >= 2 && startDayOfWeek <= 5 && diffMsForCheck > 0
      && endDayOfWeek >= 1 && endDayOfWeek <= 5) {
    const firstMonday = new Date(startForDayCheck);
    firstMonday.setDate(firstMonday.getDate() + ((8 - startDayOfWeek) % 7));
    if (firstMonday <= endForDayCheck) {
      const leadingDays = Math.max(0, 6 - startDayOfWeek); // Tue->4 ... Fri->1
      const sinceMonday = Math.round((endForDayCheck - firstMonday) / (1000 * 60 * 60 * 24));
      const fullWeeks = Math.floor(sinceMonday / 7);
      const trailingDays = endDayOfWeek - 1; // Mon=0, Tue=1, ...
      return `${leadingDays + fullWeeks * 6 + trailingDays} gün`;
    }
  }

  // For all other cases, calculate working days (excluding weekends)
  // Note: endDate is "return to work date" so it's exclusive
  const workingDays = calculateWorkingDays(startForDayCheck, endForDayCheck);
  
  // If working days is 0 but there are hours/minutes, return time-based duration
  if (workingDays === 0 && (hours > 0 || minutes > 0)) {
    let result = '';
    if (hours > 0) result += `${hours} saat`;
    if (minutes > 0) result += (result ? ' ' : '') + `${minutes} dakika`;
    if (!result) result = '0 dakika';
    return result;
  }
  
  // Return working days
  if (workingDays > 0) {
    return `${workingDays} gün`;
  }
  
  return '0 gün';
};

/**
 * Calculate working days between two dates (excluding weekends)
 * Note: endDate is "return to work date" so it's exclusive (not counted)
 * @param {Date|string} startDate - Start date (inclusive)
 * @param {Date|string} endDate - End date (exclusive - return to work date)
 * @returns {number} Number of working days
 */
export const calculateWorkingDays = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set to midnight for accurate comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  // If end date is before or equal to start date, return 0
  if (end <= start) {
    return 0;
  }
  
  let workingDays = 0;
  const current = new Date(start);
  
  // Count working days from start (inclusive) to end (exclusive)
  while (current < end) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 0 = Sunday, 6 = Saturday
      workingDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return workingDays;
}; 